import React, { useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  TooltipProps,
  Line,
  Label,
} from "recharts";
import styled from "styled-components";
import { ReactComponent as EllipseIcon } from "assets/svg/ellipse.svg";
import { ReactComponent as UnionIcon } from "assets/svg/union.svg";
import { H3 } from "melodies-source/Text";
import { Tooltip as DefaultTooltip } from "./Tooltip";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { Switch } from "Components/Switch";
import Skeleton from "react-loading-skeleton";
import { ReactComponent as DownloadIcon } from "assets/svg/download.svg";
import { debounce } from "Utils";
import { Button } from "Components/Button";
import { BaseDataItem, DataKeys } from "./types";
import { barChartToCsv } from "./utils";
import { useMobileMediaQuery } from "hooks";
import { formatNumber } from "Utils/format";
import { AxisDomain, AxisInterval } from "recharts/types/util/types";
export * from "./types";

interface AxisProps {
  tickMargin?: number;
  minTickGap?: number;
  angle?: number;
  interval?: AxisInterval;
}
interface Props<T extends BaseDataItem> {
  height: string | number;
  data: T[] | ((activeControls: string[]) => T[]);
  dataKeys: DataKeys<T>;
  title?: string;
  CustomTooltip?: React.FC<TooltipProps<ValueType, NameType>>;
  width?: string | number;
  className?: string;
  mainAxisProps?: AxisProps;
  secondaryAxisProps?: AxisProps;
  mode?: "default" | "stacked";
  barOrder?: "default" | "reverse";
  controls?: Record<string, string>;
  formatter?: (value: number) => string;
  valueFormat?: "number" | "percentage";
  loading?: boolean;
  withExport?: { reportTitle: string };
  axisLabels?: {
    left: string;
    right: string;
  };
  domain?: AxisDomain;
}

export const BarChart = <T extends BaseDataItem>({
  width = "100%",
  height,
  data,
  dataKeys,
  CustomTooltip,
  className,
  mainAxisProps: maProps,
  secondaryAxisProps: saProps,
  title,
  mode = "default",
  barOrder = "default",
  controls,
  formatter,
  valueFormat = "number",
  loading = false,
  withExport,
  axisLabels,
  domain,
}: Props<T>) => {
  const [activeControls, setActiveControls] = useState<string[]>([]);
  const isMobile = useMobileMediaQuery();
  const isDesktop = !isMobile;
  const hasLegends = Object.values(dataKeys).some(
    (config) => config.legendLabel,
  );
  const valueFormatter =
    formatter ||
    ((value: number) =>
      valueFormat === "percentage"
        ? `${Math.round(value * 100)}%`
        : formatNumber(value));

  const toggleControl = (control: string) => {
    setActiveControls((prev) =>
      prev.includes(control)
        ? prev.filter((c) => c !== control)
        : [...prev, control],
    );
  };

  const axisBaseProps = {
    tick: {
      fontSize: 14,
      fontWeight: 500,
      fill: "var(--secondary-text-color)",
    },
    axisLine: false,
    tickLine: false,
  } as const;

  const mainAxisProps = {
    type: "category",
    dataKey: "label",
    interval: "preserveStartEnd",
    tickMargin: isMobile ? 5 : 10,
    tickFormatter: (value) =>
      value.length > 6 ? value.replace(" ", "\n") : value,
    ...maProps,
  } as const;

  const secondaryAxisProps = {
    tickFormatter: valueFormatter,
    domain,
    ...saProps,
  } as const;

  const dataKeysEntries =
    barOrder === "reverse"
      ? Object.entries(dataKeys).reverse()
      : Object.entries(dataKeys);

  const [exportingData, setExportingData] = useState(false);
  function handleExportData() {
    barChartToCsv(
      typeof data === "function" ? data(activeControls) : data,
      dataKeys,
      withExport?.reportTitle,
    );
    setExportingData(false);
  }
  const debouncedExportData = debounce(handleExportData);

  const hasRightAxis = !!dataKeysEntries.find(
    ([_, config]) => config.axis === "right",
  );
  return (
    <Container data-has-controls={!!controls}>
      {title && <Title>{title}</Title>}
      {withExport && (
        <StyledButton
          leftIcon={<DownloadIcon />}
          loading={exportingData}
          disabled={loading}
          onClick={() => {
            setExportingData(true);
            debouncedExportData();
          }}
        >
          Export Data
        </StyledButton>
      )}
      {controls && (
        <Controls>
          {Object.entries(controls).map(([key, value]) => (
            <SwitchContainer key={key}>
              <Switch
                value={activeControls.includes(key)}
                onChange={() => toggleControl(key)}
              />
              <p>{value}</p>
            </SwitchContainer>
          ))}
        </Controls>
      )}
      <div style={{ width, height }} className={className}>
        <ResponsiveContainer width="100%" height="100%">
          {loading ? (
            <Skeleton height={height} />
          ) : (
            <ComposedChart
              layout={isMobile ? "vertical" : "horizontal"}
              data={typeof data === "function" ? data(activeControls) : data}
            >
              <XAxis
                xAxisId={isMobile ? "default" : undefined}
                {...axisBaseProps}
                {...(isMobile
                  ? { type: "number" }
                  : {
                      minTickGap: 15,
                      ...mainAxisProps,
                    })}
              >
                {isMobile && axisLabels?.left && (
                  <Label
                    value={axisLabels.left}
                    position="center"
                    dy={12}
                    fontSize={12}
                  />
                )}
              </XAxis>
              <YAxis
                yAxisId={isDesktop ? "default" : undefined}
                {...axisBaseProps}
                {...(isMobile ? mainAxisProps : secondaryAxisProps)}
              >
                {isDesktop && axisLabels?.left && (
                  <Label
                    value={axisLabels.left}
                    position="center"
                    angle={-90}
                    dx={-30}
                    fontSize={14}
                  />
                )}
              </YAxis>
              {hasRightAxis &&
                (isMobile ? (
                  <XAxis
                    xAxisId="right"
                    orientation="top"
                    {...axisBaseProps}
                    {...(isMobile ? { type: "number" } : secondaryAxisProps)}
                  >
                    {axisLabels?.right && (
                      <Label
                        value={axisLabels.right}
                        position="center"
                        dy={-14}
                        fontSize={12}
                      />
                    )}
                  </XAxis>
                ) : (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    {...axisBaseProps}
                    {...(isMobile ? { type: "number" } : secondaryAxisProps)}
                  >
                    {axisLabels?.right && (
                      <Label
                        value={axisLabels.right}
                        position="center"
                        angle={90}
                        dx={30}
                        fontSize={14}
                      />
                    )}
                  </YAxis>
                ))}
              <Tooltip
                content={(props) =>
                  CustomTooltip ? (
                    <CustomTooltip {...props} />
                  ) : (
                    <DefaultTooltip<T>
                      {...props}
                      dataKeys={dataKeys}
                      formatter={valueFormatter}
                    />
                  )
                }
                cursor={{ fill: "none" }}
              />
              {dataKeysEntries.map(([key, config]) =>
                config.type === "bar" ? (
                  <Bar
                    xAxisId={isMobile ? config.axis || "default" : undefined}
                    yAxisId={isDesktop ? config.axis || "default" : undefined}
                    key={key}
                    dataKey={key}
                    fill={config.color || "var(--main-color)"}
                    stackId={
                      config.stackId || mode === "stacked" ? "bar" : undefined
                    }
                    {...(isMobile && { maxBarSize: 20 })}
                  />
                ) : (
                  <Line
                    xAxisId={isMobile ? config.axis || "default" : undefined}
                    yAxisId={isDesktop ? config.axis || "default" : undefined}
                    key={key}
                    dataKey={key}
                    stroke={config.color || "var(--main-color)"}
                    strokeWidth={2}
                    dot={false}
                  />
                ),
              )}
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
      {hasLegends && (
        <Legends>
          {Object.entries(dataKeys).map(([key, config]) => {
            const Icon = config.type === "bar" ? EllipseIcon : UnionIcon;
            return (
              <Legend key={key}>
                <Icon style={{ color: config.color || "var(--main-color)" }} />
                <p>{config.legendLabel}</p>
              </Legend>
            );
          })}
        </Legends>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 35px;
`;

const Title = styled(H3)`
  [data-has-controls="false"] & {
    margin-bottom: 15px;
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  width: 100%;
  margin-bottom: 10px;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  p {
    font-weight: 600;
    color: var(--text-color);
  }
`;

const Legends = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 50px;
  row-gap: 20px;
  flex-wrap: wrap;

  ${({ theme }) => theme.media.mobile} {
    gap: 30px;
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  p {
    font-weight: 500;
    color: var(--text-color);
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  padding-left: 15px;
  padding-right: 15px;

  ${({ theme }) => theme.media.desktop} {
    position: absolute;
    align-self: flex-end;
  }
`;
